import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@emotion/react';
import {
  ProfileAddIcon,
  ProfileCropIcon,
  ProfileRoundIcon,
} from 'public/assets';
import theme from '@styles/theme';
import { CustomProfile, OnboardingPopup } from '@customTypes/chapter';

interface IChooseProfile {
  onBoarding: OnboardingPopup;
  customImage?: CustomProfile;
  onSelectMyImage: () => void;
  onConfirm: (image: CustomProfile) => void;
}

interface IProfileList {
  onBoarding: OnboardingPopup;
  customImage?: CustomProfile;
  setProfile: React.Dispatch<React.SetStateAction<CustomProfile | undefined>>;
  onSelectImage: () => void;
}

const ProfileList = ({
  onBoarding,
  customImage,
  setProfile,
  onSelectImage,
}: IProfileList) => {
  const [chooseProfile, setChooseProfile] = useState<number>(0);

  useEffect(() => {
    if (customImage) {
      const index = onBoarding.popupProfileImages.findIndex(
        (item) => item.profileImageFileId === customImage.profileImageFileId,
      );
      setChooseProfile(index);
    } else {
      setProfile(onBoarding.popupProfileImages[0]);
    }
  }, [onBoarding, customImage]);

  const onClickProfile = (index: number) => {
    setChooseProfile(index);
    setProfile(onBoarding.popupProfileImages[index]);
  };

  return (
    <div css={listContainer}>
      {onBoarding.canUploadProfileImage && (
        <div key={'add'} className="profile-choose-add" onClick={onSelectImage}>
          <ProfileAddIcon />
        </div>
      )}
      {onBoarding.popupProfileImages.map((item, index) => (
        <div
          key={item.profileImageFileId}
          className={`profile-choose-box ${
            chooseProfile === index && 'on-border'
          }`}
          onClick={() => {
            onClickProfile(index);
          }}
        >
          {chooseProfile === index && <div className="profile-choose-on" />}
          <img
            src={item.profileImageFile.link}
            className="image-box"
            alt="Profile image"
          />
        </div>
      ))}
    </div>
  );
};

const ChooseProfile = ({
  onBoarding,
  customImage,
  onSelectMyImage,
  onConfirm,
}: IChooseProfile) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<CustomProfile | undefined>(
    customImage,
  );

  return (
    <div css={container}>
      <div className="title">{t('chat_popup_title_choose_profile_image')}</div>
      <div className="profile-box">
        <div className="profile-image">
          {profile && (
            <img
              src={profile.profileImageFile?.link || profile.fileUrl}
              className="profile-image"
              alt="Profile image"
            />
          )}
          <div className="crop-container">
            <ProfileCropIcon />
          </div>
          <div className="crop-container">
            <ProfileRoundIcon />
          </div>
        </div>
      </div>
      <ProfileList
        onBoarding={onBoarding}
        setProfile={setProfile}
        onSelectImage={onSelectMyImage}
        customImage={customImage}
      />

      <button
        className="button"
        onClick={() => {
          profile && onConfirm(profile);
        }}
      >
        {t('common_screen_title_confirm')}
      </button>
    </div>
  );
};

const container = css`
  width: 100%;
  padding: 24px;
  border-radius: 12px;
  background-color: ${theme.colors.white};
  z-index: 2;

  .title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-family: 'Apple SD Gothic Neo';
      font-style: normal;
      font-weight: 700;
      font-size: 24px;
      line-height: 30px;
    }
  }

  .profile-box {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;

    .profile-image {
      position: relative;
      height: 287px;
      width: 287px;
      background-color: gray;
      object-fit: cover;
    }

    .crop-container {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
    }
  }

  .button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 13px 16px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1em;
    line-height: 22px;
    color: ${theme.colors.white};
    background-color: ${theme.colors.primary500};
    cursor: pointer;
  }
`;

const listContainer = css`
  display: flex;
  flex-direction: row;
  margin: 24px 0;
  width: 100%;
  overflow: scroll;
  overflow-y: hidden;
  gap: 14px;

  .profile-choose-box {
    position: relative;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    flex-shrink: 0;
    border: 1.4px solid ${theme.colors.gray400};
    background-color: ${theme.colors.gray300};
    cursor: pointer;

    .image-box {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      flex-shrink: 0;
      object-fit: cover;
    }
  }

  .on-border {
    border: 1.4px solid ${theme.colors.primary500};
  }

  .profile-choose-on {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    flex-shrink: 0;

    background-color: #753bbd33;
  }

  .profile-choose-add {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    cursor: pointer;
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;

export default ChooseProfile;
